/**
 * One manual-input editor's data (#687): what it shows (API_ENDPOINTS.MANUAL_INPUT)
 * and its actions (API_ENDPOINTS.MANUAL_INPUT_ACTION). Actions write straight
 * to the instance; `onApplied` lets the chain view refresh its statuses.
 */
import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from '../../../config/api';

export class ManualInputRequestError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export async function postManualInput(instanceId, editorId, action, body) {
  const res = await fetch(API_ENDPOINTS.MANUAL_INPUT_ACTION(instanceId, editorId, action), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ManualInputRequestError(data.message || data.error || res.statusText, res.status, data);
  return data;
}

export default function useManualInput(instanceId, editorId, { onApplied, withContext = true } = {}) {
  const [context, setContext] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!instanceId || !withContext) return;
    try {
      const res = await fetch(API_ENDPOINTS.MANUAL_INPUT(instanceId, editorId), {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || res.statusText);
      setContext(data);
    } catch (err) {
      setError(err.message);
    }
  }, [instanceId, editorId, withContext]);

  useEffect(() => { reload(); }, [reload]);

  /** Runs an action; resolves to the response, or null when it failed (error is set). */
  const run = useCallback(async (action, body, { rethrow = false } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const data = await postManualInput(instanceId, editorId, action, body);
      if (action === 'apply') {
        await reload();
        onApplied?.();
      }
      return data;
    } catch (err) {
      if (rethrow) throw err;
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  }, [instanceId, editorId, reload, onApplied]);

  return { context, busy, error, setError, run, reload };
}
